<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <div v-if="allowed" class="anna-call">
    <div class="anna-action-row">
      <oxd-button
        v-if="state === 'idle' || state === 'done' || state === 'error'"
        display-type="secondary"
        :label="`Start test interview (${persona})`"
        :disabled="annaDown || !!unavailable"
        @click="start"
      />
      <template v-else-if="state === 'live'">
        <oxd-button display-type="danger" label="End interview" @click="stop" />
        <oxd-button
          display-type="ghost"
          :label="muted ? 'Unmute' : 'Mute'"
          @click="toggleMute"
        />
      </template>
      <!-- the call happened; only saving it failed: try the same call again -->
      <oxd-button
        v-if="state === 'error' && callId"
        display-type="ghost"
        label="Try again to save this call"
        @click="save"
      />
      <span class="anna-call-status" :class="`anna-call-status--${state}`">
        {{ statusText }}
      </span>
    </div>
    <p v-if="state === 'idle'" class="anna-muted">
      A real call with {{ persona }} in this browser: you play the candidate
      using your microphone. It uses the assistant chosen in ANNA Settings and
      fills in this candidate's details. Nothing is sent to the candidate.
    </p>

    <p v-if="unavailable && state === 'idle'" class="anna-call-warn">
      {{ unavailable }}
    </p>
    <p v-if="advice && state === 'idle'" class="anna-call-note">{{ advice }}</p>
    <p v-if="unfilled.length && state === 'idle'" class="anna-call-warn">
      The assistant's instructions use {{ unfilled.map(braces).join(', ') }},
      which ANNA doesn't fill: they'll be empty in the call. Rename them in the
      instructions (ANNA Settings → Admin → "How Anna behaves in the interview")
      or ask for them to be supported.
    </p>

    <!-- What the interviewer will ask, phase by phase (from the server's plan); HR can edit it -->
    <div v-if="plan && state === 'idle'" class="anna-plan">
      <div class="anna-plan-top">
        <h6 class="anna-plan-title">
          {{ persona }} will ask
          <span v-if="edited" class="anna-plan-edited">edited by HR</span>
        </h6>
        <div
          v-if="interviewSlot === 'general' && !editing"
          class="anna-plan-actions"
        >
          <oxd-button
            display-type="text"
            label="Edit plan"
            @click="startEdit"
          />
          <oxd-button
            v-if="edited"
            display-type="text"
            label="Back to ANNA's plan"
            :disabled="savingPlan"
            @click="savePlan([])"
          />
        </div>
      </div>
      <p v-if="editing" class="anna-plan-help">
        One question per line. Clear a box to leave the section out. Saved plans
        are used for this candidate's next calls instead of ANNA's.
      </p>
      <div v-for="phase in plan" :key="phase.key" class="anna-plan-phase">
        <div class="anna-plan-head">
          {{ phase.title }}
          <span class="anna-plan-source">{{ phase.source }}</span>
        </div>
        <textarea
          v-if="editing"
          v-model="draft[phase.key]"
          class="anna-plan-edit"
          :rows="editRows(draft[phase.key])"
        ></textarea>
        <template v-else>
          <ol v-if="phase.questions.length" class="anna-plan-list">
            <li v-for="(q, i) in phase.questions" :key="i">
              {{ q }}
              <span v-if="phase.why && phase.why[i]" class="anna-plan-why">
                {{ phase.why[i] }}
              </span>
            </li>
          </ol>
          <p v-else class="anna-plan-empty">
            None this time. {{ emptyHint(phase) }}
          </p>
          <p
            v-for="(s, i) in phase.skipped || []"
            :key="`skip-${i}`"
            class="anna-plan-skipped"
          >
            Not asked, the CV answers it: {{ s.question }}
            <b>{{ s.answer }}</b>
            <span v-if="s.evidence">(“{{ s.evidence }}”)</span>
          </p>
        </template>
      </div>
      <div v-if="editing" class="anna-plan-actions">
        <oxd-button
          display-type="ghost"
          label="Cancel"
          @click="editing = false"
        />
        <oxd-button
          display-type="secondary"
          :label="savingPlan ? 'Saving…' : 'Save plan'"
          :disabled="savingPlan"
          @click="savePlan(draftPlan())"
        />
      </div>
      <div v-if="!editing" class="anna-plan-phase">
        <div class="anna-plan-head">
          Rest of the call
          <span class="anna-plan-source">the assistant's own instructions</span>
        </div>
        <p class="anna-plan-empty">
          Whatever the instructions add themselves, such as the candidate's own
          questions and the closing.
        </p>
      </div>
    </div>

    <div v-if="lines.length || partial" ref="log" class="anna-call-log">
      <div
        v-for="(line, i) in lines"
        :key="i"
        :class="['anna-call-line', `anna-call-line--${line.role}`]"
      >
        <span class="anna-call-who">
          {{ line.role === 'assistant' ? persona : 'You' }}
        </span>
        {{ line.text }}
      </div>
      <div v-if="partial" class="anna-call-line anna-call-line--partial">
        {{ partial }}
      </div>
    </div>
  </div>
</template>

<script>
import {APIService} from '@/core/util/services/api.service';

// Vapi finishes writing up a call a few seconds after it ends.
const SAVE_ATTEMPTS = 8;
const SAVE_DELAY_MS = 4000;

export default {
  name: 'TestInterview',
  props: {
    candidateId: {type: Number, required: true},
    // 'general' (HR) or 'technical'
    interviewSlot: {type: String, required: true},
    persona: {type: String, required: true},
    // ANNA's server is down or under maintenance: no calls until she's back
    annaDown: {type: Boolean, default: false},
    // bumped after each re-score: the plan box loads ANNA's new questions (a live call keeps going)
    planVersion: {type: Number, default: 0},
  },
  emits: ['saved'],
  setup() {
    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/anna/candidates',
    );
    // Saving is retried while the voice tool writes the call up ("The call has not
    // finished yet."), so its 400s stay quiet; save() reports the last one itself.
    const saveHttp = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/anna/candidates',
    );
    saveHttp.setIgnorePath('.*');
    return {http, saveHttp};
  },
  data() {
    return {
      state: 'idle', // idle | connecting | live | saving | done | error
      error: '',
      muted: false,
      lines: [],
      partial: '',
      callId: null,
      // null until loaded; false = this user can't run test interviews (not admin)
      allowed: true,
      plan: null,
      // editing the plan: one text per phase key
      editing: false,
      draft: {},
      savingPlan: false,
      unfilled: [],
      advice: null,
      // why the plan couldn't be loaded (ANNA away, or a setup step), if it couldn't
      unavailable: '',
    };
  },
  computed: {
    edited() {
      return !!this.plan?.some((p) => p.source === 'edited by HR');
    },
    url() {
      return `/api/v2/anna/candidates/${this.candidateId}/interview/${this.interviewSlot}`;
    },
    statusText() {
      return {
        idle: '',
        connecting: 'Connecting… allow the microphone if the browser asks.',
        live: '● Live',
        saving:
          'Call ended. Fetching the transcript and recording, and ANNA is reviewing the interview (up to a minute)…',
        done: 'Saved to this candidate.',
        error: this.error,
      }[this.state];
    },
  },
  watch: {
    planVersion() {
      this.loadPlan();
    },
  },
  created() {
    this.loadPlan();
  },
  beforeUnmount() {
    this.vapi?.stop();
  },
  methods: {
    emptyHint(phase) {
      if (this.edited) return '';
      if (phase.key === 'history') {
        return "Built from the CV's timeline: score or re-score the CV to fill it (or the block is off for this vacancy).";
      }
      if (phase.key === 'role') {
        return "Add questions in the vacancy's Interview section (or the block is off there).";
      }
      return "The block is off in the vacancy's Interview section, or its list in ANNA Settings is empty.";
    },
    // a line per question, plus the wrapping of long ones
    editRows(text) {
      const lines = (text || '').split('\n');
      return Math.max(
        2,
        lines.reduce((n, l) => n + Math.ceil((l.length || 1) / 110), 1),
      );
    },
    startEdit() {
      const draft = {};
      this.plan.forEach((p) => {
        draft[p.key] = p.questions.join('\n');
      });
      this.draft = draft;
      this.editing = true;
    },
    draftPlan() {
      return this.plan.map((p) => ({
        key: p.key,
        questions: (this.draft[p.key] || '')
          .split('\n')
          .map((q) => q.trim())
          .filter(Boolean),
      }));
    },
    // [] goes back to ANNA's own plan
    savePlan(plan) {
      this.savingPlan = true;
      this.http
        .request({
          method: 'PUT',
          url: `/api/v2/anna/candidates/${this.candidateId}/plan`,
          data: {plan},
        })
        .then(() => {
          this.editing = false;
          return this.loadPlan();
        })
        .then(() => this.$toast.saveSuccess())
        .finally(() => {
          this.savingPlan = false;
        });
    },
    braces(name) {
      return `{{${name}}}`;
    },
    // Plain fetch: a 403 for non-admins should hide this, not raise the API error toast.
    async loadPlan() {
      try {
        const response = await fetch(`${window.appGlobal.baseUrl}${this.url}`, {
          credentials: 'same-origin',
          headers: {Accept: 'application/json'},
        });
        if (response.status === 403 || response.status === 401) {
          this.allowed = false;
          return;
        }
        const body = await response.json().catch(() => ({}));
        if (response.ok) {
          this.plan = body.data.plan;
          this.unfilled = body.data.unfilled || [];
          this.advice = body.data.advice;
          this.unavailable = body.data.web ? '' : body.data.setupNeeded || '';
        } else {
          this.unavailable =
            body?.error?.message || 'ANNA is unavailable right now.';
        }
      } catch (e) {
        this.plan = null;
      }
    },
    async start() {
      this.state = 'connecting';
      this.lines = [];
      this.partial = '';
      try {
        const {
          data: {data: session},
        } = await this.http.request({method: 'GET', url: this.url});
        // the page can only start calls with the voice tool it knows
        if (session.web?.provider !== 'vapi') {
          this.fail(
            session.setupNeeded ||
              "ANNA's voice tool does not support browser test calls.",
          );
          return;
        }
        // loaded only when a call starts
        // publicPath is '.' in vue.config.js, so lazy chunks would load relative to the
        // page URL; point webpack at dist/ before loading the Vapi SDK chunk.
        // eslint-disable-next-line no-undef, no-global-assign
        __webpack_public_path__ = `${window.appGlobal.publicPath}/dist/`;
        const {default: Vapi} = await import('@vapi-ai/web');
        this.vapi = new Vapi(session.web.publicKey);
        this.vapi.on('call-start', () => (this.state = 'live'));
        this.vapi.on('call-end', () => this.save());
        this.vapi.on('message', this.onMessage);
        this.vapi.on('error', (e) => this.fail(e));
        const call = await this.vapi.start(
          session.web.assistantId,
          session.web.overrides,
        );
        this.callId = call?.id || null;
        if (!this.callId) this.fail('Vapi did not start the call.');
      } catch (e) {
        // API errors already show the toast; keep the reason here too
        this.fail(e);
      }
    },
    stop() {
      this.vapi?.stop();
    },
    toggleMute() {
      this.muted = !this.muted;
      this.vapi?.setMuted(this.muted);
    },
    onMessage(message) {
      if (message.type !== 'transcript') return;
      if (message.transcriptType === 'final') {
        this.lines.push({role: message.role, text: message.transcript});
        this.partial = '';
      } else {
        this.partial = message.transcript;
      }
      this.$nextTick(() => {
        if (this.$refs.log)
          this.$refs.log.scrollTop = this.$refs.log.scrollHeight;
      });
    },
    async save() {
      if (!this.callId || this.state === 'saving' || this.state === 'done')
        return;
      this.state = 'saving';
      this.partial = '';
      for (let attempt = 1; attempt <= SAVE_ATTEMPTS; attempt++) {
        await new Promise((resolve) => setTimeout(resolve, SAVE_DELAY_MS));
        try {
          const {
            data: {data: review},
          } = await this.saveHttp.request({
            method: 'PUT',
            url: this.url,
            data: {callId: this.callId},
            timeout: 180000,
          });
          this.state = 'done';
          this.$emit('saved', review);
          return;
        } catch (e) {
          if (attempt === SAVE_ATTEMPTS) this.fail(e);
        }
      }
    },
    fail(e) {
      const detail =
        e?.response?.data?.error?.message ||
        e?.data?.error?.message ||
        e?.error?.message ||
        e?.errorMsg ||
        e?.message ||
        (typeof e === 'string' ? e : '');
      // what failed: starting the call, the call itself, or saving it afterwards
      const what = {
        saving: ['Could not finish', 'Could not finish the call.'],
        live: ['The call stopped', 'The call stopped.'],
      }[this.state] || ['Could not start', 'Could not start the call.'];
      this.error = detail ? `${what[0]}: ${detail}` : what[1];
      this.state = 'error';
    },
  },
};
</script>

<style lang="scss" scoped>
.anna-call {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.anna-action-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}
.anna-muted {
  margin: 0;
  color: #6a7482;
  font-size: 13px;
}
.anna-call-status {
  font-size: 13px;
  font-weight: 700;
  color: #6a7482;
  &--live {
    color: #c0392b;
  }
  &--done {
    color: #2e8b45;
  }
  &--error {
    color: #c0392b;
    font-weight: 600;
  }
}
.anna-call-note,
.anna-call-warn {
  margin: 0;
  font-size: 13px;
  border-radius: 8px;
  padding: 8px 12px;
}
.anna-call-note {
  color: #1f5fa8;
  background: #eaf2fb;
}
.anna-call-warn {
  color: #a67c00;
  background: #fdf6d8;
}
.anna-plan {
  border: 1px solid #e7ebf1;
  border-radius: 12px;
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.anna-plan-title {
  margin: 0;
  font-size: 13px;
  font-weight: 800;
  color: #12233f;
}
.anna-plan-head {
  font-size: 12px;
  font-weight: 800;
  color: #5b6672;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.anna-plan-source {
  margin-left: 6px;
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0;
  color: #9aa4b1;
}
.anna-plan-list {
  margin: 4px 0 0;
  padding-left: 20px;
  font-size: 13px;
  line-height: 1.6;
  color: #16202b;
}
.anna-plan-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}
.anna-plan-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}
.anna-plan-edited {
  font-size: 11px;
  font-weight: 700;
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 999px;
  background: #fff3d6;
  color: #a05a00;
}
.anna-plan-help {
  font-size: 12px;
  color: #6a7482;
  margin: 0 0 8px;
}
.anna-plan-why {
  display: block;
  font-size: 12px;
  color: #8a94a6;
}
.anna-plan-skipped {
  font-size: 12px;
  color: #6a7482;
  margin: 4px 0 0;
}
.anna-plan-edit {
  width: 100%;
  font: inherit;
  font-size: 13px;
  line-height: 1.5;
  border: 1px solid #dde3ea;
  border-radius: 8px;
  padding: 8px 10px;
  resize: vertical;
}
.anna-plan-empty {
  margin: 4px 0 0;
  font-size: 13px;
  color: #9aa4b1;
}
.anna-call-log {
  max-height: 320px;
  overflow-y: auto;
  padding: 12px 14px;
  border: 1px solid #e7ebf1;
  border-radius: 12px;
  background: #fbfcfd;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.anna-call-line {
  font-size: 13px;
  line-height: 1.5;
  color: #16202b;
  &--user {
    color: #1f5fa8;
  }
  &--partial {
    color: #9aa4b1;
    font-style: italic;
  }
}
.anna-call-who {
  font-weight: 800;
  margin-right: 6px;
}
</style>
